import { P } from '@piccolohealth/util';
import React from 'react';
import { ChakraV3 } from '..';

interface Props {
  value: string;
  onChange: (value: string) => void;
  fieldCount: number;
}

export const PinInput = React.forwardRef<HTMLInputElement, Props>((props, ref) => {
  const { value = '', onChange, fieldCount } = props;

  const chars = P.range(0, fieldCount).map((i) => value[i] ?? ' ');
  const inputRefs = React.useRef<(HTMLInputElement | null)[]>([]);

  const focusInput = (index: number) => {
    inputRefs.current[index]?.focus();
  };

  const updateValue = (next: string[]) => {
    onChange(next.join('').trimEnd());
  };

  const onCharChange = (index: number, inputValue: string) => {
    const digit = inputValue.trim().slice(-1);

    // if it's not a number dont do anything
    if (digit && !/^\d$/.test(digit)) {
      return;
    }

    const next = [...chars];
    next[index] = digit || ' ';
    updateValue(next);

    if (digit && index < fieldCount - 1) {
      focusInput(index + 1);
    }
  };

  const onKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace') {
      e.preventDefault();
      const next = [...chars];

      if (chars[index].trim()) {
        next[index] = ' ';
        updateValue(next);
      } else if (index > 0) {
        next[index - 1] = ' ';
        updateValue(next);
        focusInput(index - 1);
      }
    }

    if (e.key === 'ArrowLeft' && index > 0) {
      focusInput(index - 1);
    }

    if (e.key === 'ArrowRight' && index < fieldCount - 1) {
      focusInput(index + 1);
    }
  };

  const onFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    e.target.select();
  };

  return (
    <ChakraV3.HStack>
      {chars.map((char, i) => (
        <ChakraV3.Input
          key={i}
          ref={(el) => {
            inputRefs.current[i] = el;
            return i === 0 ? ref : undefined;
          }}
          data-index={i}
          value={char.trim()}
          onChange={(e) => onCharChange(i, e.target.value)}
          onKeyDown={(e) => onKeyDown(i, e)}
          onFocus={onFocus}
          inputMode='numeric'
          autoComplete='one-time-code'
          w='2.5rem'
          h='2.5rem'
          textAlign='center'
          fontSize='sm'
          fontWeight='bold'
          placeholder='○'
          _focus={{
            _placeholder: { opacity: 0 },
          }}
        />
      ))}
    </ChakraV3.HStack>
  );
});
